/** Public job pages — a deliberately tiny standalone entry.
 *
 * Served by Caddy at /jobs and /jobs/<slug> (try_files → /public-jobs.html);
 * no React, no MUI, no auth — anonymous candidates get a fast page. Data
 * comes from the AllowAny /api/v2/jobs/ endpoints, which tenant-scope by
 * the request Host header.
 */

import { jobMetaLine, salaryLine } from "./ad-header";
import { lookFromParams } from "./look";
import { renderMarkdown } from "./markdown";
import {
	languageName,
	type PublicJobStrings,
	preferredLanguage,
	strings,
} from "./strings";
import "./styles.css";

interface PublicJobListItem {
	/** Absolute address on the owning tenant's host. */
	url: string;
	slug: string;
	public_title: string;
	/** One line for the card in a list — written per language, not cut from
	 *  the body. Empty on ads written before the field existed. */
	teaser: string;
	location_text: string;
	employment_type: string;
	company_name: string | null;
	published_at: string | null;
	/** The language this payload is written in, and the ones the reader can
	 *  switch to. */
	language: string;
	languages: string[];
}

/** The look the customer picked for their pages and embeds. Empty strings
 *  where they chose nothing, so the stylesheet keeps its own values. */
interface JobBrand {
	font_stack: string;
	accent_color: string;
	accent_text_color: string;
	text_color: string;
	surface_color: string;
	background_color: string;
	/** The page on the customer's own site that carries the ad embed. */
	job_page_url: string;
	agency_name: string;
}

interface ScreeningQuestion {
	id: number;
	text: string;
}

interface PublicJobDetail extends PublicJobListItem {
	public_description: string;
	salary_min: number | null;
	salary_max: number | null;
	salary_currency: string;
	salary_period: string;
	agency_name: string;
	skills: string[];
	valid_through: string | null;
	/** What this ad demands of an applicant: nothing, a written cover letter,
	 *  or an answer to every question below. Never a mix. */
	application_requirement: "NONE" | "COVER_LETTER" | "QUESTIONS";
	questions: ScreeningQuestion[];
}

interface ApplicationStatus {
	job_title: string;
	applied_at: string;
	/** The language the candidate applied in — this page speaks it. */
	language: string;
	state: "received" | "under_review" | "moving_forward" | "answered";
	/** The recruiter is taking the application further — shown as its own
	 *  banner, before and after the yes has gone out. */
	taken_further: boolean;
}

interface ParsedRole {
	company: string;
	title: string;
	start_date: string;
	end_date: string;
	current: boolean;
}

interface ParsedDraft {
	name: string;
	headline: string;
	emails: string[];
	phones: string[];
	skills: string[];
	roles: ParsedRole[];
}

interface ParseCvResponse {
	parse_id: number;
	draft: ParsedDraft;
	linkedin_url: string;
}

/** Set by a successful parse; echoed on apply so the CV isn't parsed twice. */
let parseId: number | null = null;

/** The language everything around the ad is written in, and its words. It
 *  follows the ad being rendered, so a Swedish ad gets a Swedish form. */
let language = "en";
let t: PublicJobStrings = strings(language);

function setLanguage(next: string): void {
	language = next;
	t = strings(next);
	document.documentElement.lang = next;
}

/** How this page is being shown: on our own host, or inside a customer's
 *  page. "list" is their roles, "ad" one role with its form. */
type EmbedMode = "list" | "ad" | null;

const params = new URLSearchParams(window.location.search);

function embedMode(): EmbedMode {
	const mode = params.get("embed");
	return mode === "list" || mode === "ad" ? mode : null;
}

/** Where a job card should take the reader.
 *
 * Embedded, that is the customer's own page: the host script tells us which
 * one it is on (``page``), and a customer who keeps their ads on a different
 * page names it in the snippet or in their settings. The slug rides in a
 * query parameter the host script reads back, so the link a candidate shares
 * is on their domain. Unembedded, our own page.
 */
function jobHref(job: PublicJobListItem, brand: JobBrand | null): string {
	if (embedMode() === null) return job.url;
	const host = params.get("page") || brand?.job_page_url;
	if (!host) return job.url;
	const url = new URL(host);
	url.searchParams.set("job", job.slug);
	return url.toString();
}

/** Tell the page around us what we need from it: how tall we are, which ad is
 *  open, and where to scroll. Ignored when we are not in a frame. */
function postToHost(message: Record<string, unknown>): void {
	if (window.parent === window) return;
	window.parent.postMessage({ source: "odi-jobs", ...message }, "*");
}

let heightObserver: ResizeObserver | null = null;

/** Report our height whenever it changes — the frame around us has none of
 *  its own, so this is what keeps the customer's page the right length. */
function reportHeightContinuously(): void {
	if (window.parent === window || heightObserver !== null) return;
	const send = (): void =>
		postToHost({
			type: "height",
			px: Math.ceil(document.documentElement.getBoundingClientRect().height),
		});
	heightObserver = new ResizeObserver(send);
	heightObserver.observe(document.documentElement);
	window.addEventListener("load", send);
	send();
}

async function loadBrand(): Promise<JobBrand | null> {
	const response = await fetch("/api/v2/jobs/brand/");
	if (!response.ok) return null;
	return (await response.json()) as JobBrand;
}

/** Paint the look onto the CSS variables the stylesheet uses.
 *
 * Two sources, in this order: what the host page was measured to look like
 * (embedded only — the frame inherits no CSS, so the host script hands the
 * values over), then what the customer set in ODI. An unset value at both
 * levels leaves our own default, which is what makes a half-filled brand and
 * an unmeasurable page equally safe.
 *
 * The measured values are query parameters, so they are validated in look.ts
 * before they arrive here: anyone can write a URL, including to our own
 * hosted ad pages.
 */
function applyBrand(brand: JobBrand | null): void {
	const measured = embedMode() === null ? null : lookFromParams(params);
	const style = document.documentElement.style;
	const painted: [string, string][] = [
		["--font-stack", measured?.font || brand?.font_stack || ""],
		["--accent", measured?.accent || brand?.accent_color || ""],
		["--accent-text", measured?.accentText || brand?.accent_text_color || ""],
		["--text", measured?.text || brand?.text_color || ""],
		["--surface", brand?.surface_color || ""],
		["--bg", measured?.bg || brand?.background_color || ""],
	];
	for (const [name, value] of painted) {
		if (value) style.setProperty(name, value);
	}
	// A web font is not available by name alone: the frame has to load the
	// same stylesheet the host page does — or, where the page serves the font
	// itself, the same file under the same family name.
	if (measured?.fontCss) {
		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = measured.fontCss;
		document.head.appendChild(link);
	} else if (measured?.fontFamily && measured.fontFile) {
		const face = document.createElement("style");
		face.textContent = `@font-face{font-family:${measured.fontFamily};src:url(${measured.fontFile});font-display:swap}`;
		document.head.appendChild(face);
	}
}

function esc(text: string): string {
	return text
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;");
}

function root(): HTMLElement {
	const element = document.getElementById("root");
	if (element === null) throw new Error("missing #root");
	return element;
}

/** Slug from the URL.
 *
 * On the jobs hosts the ad lives at /<slug>; via the main host's /jobs path
 * alias at /jobs/<slug>; ?slug=<slug> when opened as /public-jobs.html in dev.
 */
function currentSlug(): string | null {
	const path = window.location.pathname.replace(/^\/jobs(?=\/|$)/, "");
	const match = path.match(/^\/([^/]+)\/?$/);
	if (match && match[1] !== "public-jobs.html") {
		return decodeURIComponent(match[1]);
	}
	return new URLSearchParams(window.location.search).get("slug");
}

/** /application/<token> (or /jobs/application/<token>) — the status page. */
function currentStatusToken(): string | null {
	const path = window.location.pathname.replace(/^\/jobs(?=\/|$)/, "");
	const match = path.match(/^\/application\/([^/]+)\/?$/);
	return match ? decodeURIComponent(match[1]) : null;
}

function statusTimeline(): { id: string; label: string; hint: string }[] {
	return [
		{ id: "received", label: t.statusReceived, hint: t.statusReceivedHint },
		{
			id: "under_review",
			label: t.statusUnderReview,
			hint: t.statusUnderReviewHint,
		},
		{ id: "answered", label: t.statusAnswered, hint: t.statusAnsweredHint },
	];
}

async function renderStatusPage(token: string): Promise<void> {
	const response = await fetch(
		`/api/v2/jobs/application/${encodeURIComponent(token)}/`
	);
	if (!response.ok) {
		renderFailure(t.applicationNotFound);
		return;
	}
	const info = (await response.json()) as ApplicationStatus;
	setLanguage(info.language);
	document.title = `${t.yourApplication} — ${info.job_title}`;
	// moving_forward sits between review and answer but must not look like a
	// passed step for candidates who were answered with a rejection — it is
	// shown as its own banner instead of a timeline entry.
	const timelineState = info.state === "moving_forward" ? "under_review" : info.state;
	const timeline = statusTimeline();
	const activeIndex = timeline.findIndex((step) => step.id === timelineState);
	const steps = timeline
		.map((step, index) => {
			const classes = [
				"status-step",
				index <= activeIndex ? "done" : "",
				index === activeIndex ? "current" : "",
			]
				.filter(Boolean)
				.join(" ");
			const hint =
				index === activeIndex
					? `<div class="muted">${esc(step.hint)}</div>`
					: "";
			return `<li class="${classes}"><strong>${esc(step.label)}</strong>${hint}</li>`;
		})
		.join("");
	const forwardBanner = info.taken_further
		? `<div class="message success">${esc(t.takenFurther)}</div>`
		: "";
	root().innerHTML = `
		<div class="page">
			<div class="card ad-card">
				<h1 class="ad-title">${esc(t.yourApplication)}</h1>
				<div class="job-meta">${esc(info.job_title)} · ${esc(t.appliedOn)} ${esc(info.applied_at.slice(0, 10))}</div>
				${forwardBanner}
				<ol class="status-steps">${steps}</ol>
			</div>
		</div>`;
}

async function renderList(brand: JobBrand | null): Promise<void> {
	const asked = params.get("lang");
	const response = await fetch(
		asked ? `/api/v2/jobs/?lang=${encodeURIComponent(asked)}` : "/api/v2/jobs/"
	);
	if (!response.ok) {
		renderFailure(t.listFailed);
		return;
	}
	const jobs = (await response.json()) as PublicJobListItem[];
	// A list spans several ads, each with its own languages; the reader's own
	// language decides, falling back to the first ad's.
	setLanguage(preferredLanguage(jobs[0]?.languages ?? ["en"], asked));
	document.title = t.openPositions;
	// Embedded, a card has to take the whole browser to the customer's job
	// page — a link inside a frame would otherwise load the ad in the frame.
	const target = embedMode() === null ? "" : ' target="_top"';
	const cards =
		jobs.length === 0
			? `<div class="empty">${esc(t.noPositions)}</div>`
			: jobs
					.map(
						(job) => `
			<div class="card ad-card">
				<a class="job-link" href="${esc(jobHref(job, brand))}"${target}>
					<h2 class="job-title">${esc(job.public_title)}</h2>
					<div class="job-meta">${esc(jobMetaLine(job))}</div>
					${job.teaser ? `<p class="job-teaser">${esc(job.teaser)}</p>` : ""}
				</a>
			</div>`
					)
					.join("");
	// Embedded, the customer's own page carries the heading.
	const heading = embedMode() === null ? `<h1>${esc(t.openPositions)}</h1>` : "";
	root().innerHTML = `
		<div class="page">
			${heading}
			${cards}
		</div>`;
}

function injectJsonLd(ad: PublicJobDetail): void {
	const data: Record<string, unknown> = {
		"@context": "https://schema.org/",
		"@type": "JobPosting",
		title: ad.public_title,
		description: renderMarkdown(ad.public_description),
		datePosted: ad.published_at,
		employmentType: ad.employment_type,
		hiringOrganization: {
			"@type": "Organization",
			name: ad.company_name ?? ad.agency_name,
		},
		...(ad.location_text
			? {
					jobLocation: {
						"@type": "Place",
						address: {
							"@type": "PostalAddress",
							addressLocality: ad.location_text,
						},
					},
				}
			: {}),
		...(ad.skills.length ? { skills: ad.skills.join(", ") } : {}),
		...(ad.salary_min !== null || ad.salary_max !== null
			? {
					baseSalary: {
						"@type": "MonetaryAmount",
						currency: ad.salary_currency,
						value: {
							"@type": "QuantitativeValue",
							...(ad.salary_min !== null
								? { minValue: ad.salary_min }
								: {}),
							...(ad.salary_max !== null
								? { maxValue: ad.salary_max }
								: {}),
							unitText: ad.salary_period,
						},
					},
				}
			: {}),
		...(ad.valid_through ? { validThrough: ad.valid_through } : {}),
	};
	const script = document.createElement("script");
	script.type = "application/ld+json";
	script.textContent = JSON.stringify(data);
	document.head.appendChild(script);
}

const UPLOAD_ICON_SVG = `
	<svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
		<path fill="currentColor" d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-5H3v5a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-5h-2z"/>
		<path fill="currentColor" d="M11 6.83 8.41 9.41 7 8l5-5 5 5-1.41 1.41L13 6.83V16h-2V6.83z"/>
	</svg>`;

/** The screening block: the questions to answer, or the cover-letter field.
 *
 * The ad decides which (application_requirement) and whether it is mandatory:
 * QUESTIONS asks the questions, COVER_LETTER demands a letter, NONE keeps the
 * letter the optional extra it has always been. */
function screeningHtml(ad: PublicJobDetail): string {
	if (ad.application_requirement === "QUESTIONS") {
		const questions = ad.questions
			.map(
				(question, index) => `
				<div class="field question">
					<label for="question-${index}">${index + 1}. ${esc(question.text)} *</label>
					<textarea id="question-${index}" data-question-id="${question.id}" required placeholder="${esc(t.questionPlaceholder)}"></textarea>
				</div>`
			)
			.join("");
		return `
			<div class="questions">
				<div class="questions-intro">${esc(t.questionsIntro)}</div>
				${questions}
			</div>`;
	}
	const isRequired = ad.application_requirement === "COVER_LETTER";
	return `
		<div class="field">
			<label for="message">${esc(t.coverLetter)}${isRequired ? " *" : ""}</label>
			<input type="file" id="cover-letter-file" accept=".pdf,.docx,.txt" class="file-input" />
			<label for="cover-letter-file" class="dropzone" id="letter-dropzone">
				<span class="dropzone-icon">${UPLOAD_ICON_SVG}</span>
				<span class="dropzone-title" id="letter-name">${esc(t.letterDropTitle)}</span>
				<span class="dropzone-hint">${esc(t.letterDropHint)}</span>
			</label>
			<textarea id="message" name="message" ${isRequired ? "required" : ""} placeholder="${esc(t.letterPlaceholder)}"></textarea>
		</div>`;
}

function applyFormHtml(ad: PublicJobDetail): string {
	const readsFirst =
		ad.application_requirement === "QUESTIONS"
			? t.stepReadsAnswers
			: t.stepReadsLetter;
	return `
		<div class="card apply">
			<h2>${esc(t.applyHeading)}</h2>
			<p class="apply-promise">${esc(t.applyPromise)}</p>
			<ol class="process-steps">
				<li>${t.stepApply}</li>
				<li>${readsFirst}</li>
				<li>${t.stepAlwaysAnswer}</li>
			</ol>
			<div id="form-message"></div>
			<form id="apply-form">
				<input type="file" id="cv" name="cv" accept=".pdf,.docx,.txt" class="file-input" />
				<label for="cv" class="dropzone" id="cv-dropzone">
					<span class="dropzone-icon">${UPLOAD_ICON_SVG}</span>
					<span class="dropzone-title" id="cv-name">${esc(t.cvDropTitle)}</span>
					<span class="dropzone-hint">${esc(t.cvDropHint)}</span>
				</label>
				<div id="parsed-panel"></div>
				<div class="field">
					<label for="applicant_name">${esc(t.name)} *</label>
					<input type="text" id="applicant_name" name="applicant_name" required />
				</div>
				<div class="field">
					<label for="email">${esc(t.email)} *</label>
					<input type="email" id="email" name="email" required />
				</div>
				<div class="field">
					<label for="phone">${esc(t.phone)} *</label>
					<input type="tel" id="phone" name="phone" required />
				</div>
				<div class="field">
					<label for="linkedin_url">${esc(t.linkedin)}</label>
					<input type="url" id="linkedin_url" name="linkedin_url" placeholder="https://www.linkedin.com/in/..." />
				</div>
				${screeningHtml(ad)}
				<label class="consent">
					<input type="checkbox" id="consent" name="consent" required />
					<span>${esc(t.consent)} *</span>
				</label>
				<button class="submit" type="button" id="submit-button">${esc(t.holdToSubmit)}</button>
			</form>
		</div>`;
}

interface SubmittedAnswer {
	question: number;
	answer: string;
}

/** The answered screening questions, in the order they are on the page. */
function collectAnswers(form: HTMLFormElement): SubmittedAnswer[] {
	return [...form.querySelectorAll("textarea[data-question-id]")].map((element) => {
		const textarea = element as HTMLTextAreaElement;
		return {
			question: Number(textarea.dataset.questionId),
			answer: textarea.value.trim(),
		};
	});
}

function showFormMessage(kind: "error" | "success", text: string): void {
	const box = document.getElementById("form-message");
	if (box) box.innerHTML = `<div class="message ${kind}">${esc(text)}</div>`;
}

async function submitApplication(slug: string, form: HTMLFormElement): Promise<void> {
	const data = new FormData(form);
	const cv = data.get("cv");
	const hasCv = cv instanceof File && cv.size > 0;
	if (!hasCv) data.delete("cv");
	if (!hasCv && data.get("linkedin_url") === "") {
		showFormMessage("error", t.needCvOrLinkedin);
		return;
	}
	if (hasCv && parseId !== null) data.set("parse_id", String(parseId));
	// Answers ride along as one JSON field — the body is multipart because of
	// the CV, which cannot carry a nested list.
	data.set("answers", JSON.stringify(collectAnswers(form)));
	data.set("consent", "true");
	// What they read the ad in — every message back is written in it.
	data.set("language", language);
	const button = form.querySelector("button");
	if (button) button.disabled = true;
	const restoreButton = (): void => {
		if (!button) return;
		button.disabled = false;
		button.classList.remove("holding", "ready", "submitting");
		button.textContent = t.holdToSubmit;
	};
	const response = await fetch(`/api/v2/jobs/${encodeURIComponent(slug)}/apply/`, {
		method: "POST",
		body: data,
	});
	if (response.status === 201) {
		const result = (await response.json().catch(() => null)) as {
			status_url?: string;
		} | null;
		form.parentElement?.classList.add("done");
		form.remove();
		const statusLink = result?.status_url
			? ` ${esc(t.submittedFollow)} <a href="${esc(result.status_url)}">${esc(result.status_url)}</a>.`
			: "";
		const box = document.getElementById("form-message");
		if (box) {
			box.innerHTML = `<div class="message success">${esc(t.submitted)}${statusLink}</div>`;
		}
		return;
	}
	restoreButton();
	const body = (await response.json().catch(() => null)) as
		| Record<string, unknown>
		| string[]
		| null;
	const detail = Array.isArray(body)
		? body[0]
		: body && typeof body === "object"
			? Object.values(body).flat()[0]
			: null;
	showFormMessage("error", typeof detail === "string" ? detail : t.submitFailed);
}

async function renderDetail(
	slug: string,
	brand: JobBrand | null,
	asked: string | null
): Promise<void> {
	const query = asked ? `?lang=${encodeURIComponent(asked)}` : "";
	const response = await fetch(`/api/v2/jobs/${encodeURIComponent(slug)}/${query}`);
	if (response.status === 303) {
		const { redirect_to } = (await response.json()) as { redirect_to: string };
		window.location.replace(redirect_to);
		return;
	}
	if (response.status === 404) {
		renderFailure(t.adUnavailable);
		return;
	}
	if (!response.ok) {
		renderFailure(t.adFailed);
		return;
	}
	const ad = (await response.json()) as PublicJobDetail;
	// Ask again in the reader's own language when the ad has it and nothing
	// was asked for — one extra call, only on a multi-language ad.
	const wanted = preferredLanguage(ad.languages, asked);
	if (wanted !== ad.language) {
		await renderDetail(slug, brand, wanted);
		return;
	}
	setLanguage(ad.language);
	renderAd(ad, true, brand);
}

/** The other languages this ad exists in, as links that reload it. */
function languageSwitchHtml(ad: PublicJobDetail): string {
	if (ad.languages.length < 2) return "";
	const links = ad.languages
		.map((code) => {
			const label = esc(languageName(code));
			if (code === ad.language)
				return `<span class="lang current">${label}</span>`;
			const href = `?lang=${encodeURIComponent(code)}${
				embedMode() ? `&embed=ad&job=${encodeURIComponent(ad.slug)}` : ""
			}`;
			return `<a class="lang" href="${href}">${label}</a>`;
		})
		.join("");
	return `<div class="lang-switch" aria-label="${esc(t.languageLabel)}">${links}</div>`;
}

/** Build the ad page. `interactive` wires the apply form and injects JSON-LD;
 *  the wizard preview renders the identical markup with both left off, so a
 *  draft can never be submitted or indexed. */
function renderAd(
	ad: PublicJobDetail,
	interactive: boolean,
	brand: JobBrand | null = null
): void {
	document.title = ad.public_title;
	// Embedded, the tab title belongs to the customer's page, not to us.
	postToHost({ type: "job-open", slug: ad.slug, title: ad.public_title });
	// Inside a frame the markup is not what search engines read — the ad they
	// index is our own page, which carries the same payload.
	if (interactive && embedMode() === null) injectJsonLd(ad);
	const salary = salaryLine(ad);
	const salaryHtml = salary ? `<div class="salary-line">${esc(salary)}</div>` : "";
	const skillChips = ad.skills.length
		? `<div class="skill-chips">${ad.skills
				.map((skill) => `<span class="skill-chip">${esc(skill)}</span>`)
				.join("")}</div>`
		: "";
	root().innerHTML = `
		<div class="page">
			<div class="ad-top">
				<div class="brand">${esc(ad.company_name ?? ad.agency_name)}</div>
				${languageSwitchHtml(ad)}
			</div>
			<div class="card ad-card">
				<h1 class="ad-title">${esc(ad.public_title)}</h1>
				<div class="job-meta">${esc(jobMetaLine(ad))}</div>
				${salaryHtml}
				${skillChips}
				<div class="ad-body">${renderMarkdown(ad.public_description)}</div>
			</div>
			${applyFormHtml(ad)}
			<div id="more-roles"></div>
		</div>`;
	void renderMoreRoles(ad.slug, brand);
	if (!interactive) return;
	const form = document.getElementById("apply-form");
	if (form instanceof HTMLFormElement) {
		// Submission happens only through the hold-to-submit button — Enter in
		// a field (or a bot calling form.submit()) does nothing.
		form.addEventListener("submit", (event) => event.preventDefault());
		wireHoldToSubmit(ad.slug, form);
	}
	wireDropzone("cv-dropzone", "cv", (file) => {
		const cvName = document.getElementById("cv-name");
		if (cvName) void parseCv(ad.slug, file, cvName);
	});
	if (ad.application_requirement !== "QUESTIONS") {
		wireDropzone("letter-dropzone", "cover-letter-file", (file) => {
			void extractLetter(ad.slug, file);
		});
	}
}

/** Other published ads on this host, linked below the apply form. */
async function renderMoreRoles(
	currentSlug: string,
	brand: JobBrand | null
): Promise<void> {
	const container = document.getElementById("more-roles");
	if (!container) return;
	const response = await fetch(`/api/v2/jobs/?lang=${encodeURIComponent(language)}`);
	if (!response.ok) return;
	const jobs = (await response.json()) as PublicJobListItem[];
	const others = jobs.filter((job) => job.slug !== currentSlug).slice(0, 4);
	if (others.length === 0) return;
	const target = embedMode() === null ? "" : ' target="_top"';
	const cards = others
		.map(
			(job) => `
		<div class="card ad-card">
			<a class="job-link" href="${esc(jobHref(job, brand))}"${target}>
				<h3 class="job-title">${esc(job.public_title)}</h3>
				<div class="job-meta">${esc(jobMetaLine(job))}</div>
			</a>
		</div>`
		)
		.join("");
	container.innerHTML = `<h2 class="more-roles-title">${esc(t.morePositions)}</h2>${cards}`;
}

/** Hold-to-submit: press and hold until the button turns green and says
 * "Release to submit!", then let go. A cheap human check — scripted clicks
 * don't produce a held pointer, and the hold duration is random per attempt
 * so the timing can't be replayed. Works with keyboard too (hold
 * Space/Enter). */
const HOLD_MIN_MS = 900;
const HOLD_MAX_MS = 2400;
// Releasing must happen INSIDE this window after the button turns green —
// holding forever and releasing whenever no longer passes. Generous enough
// for slow reactions.
const RELEASE_WINDOW_MS = 4000;

function wireHoldToSubmit(slug: string, form: HTMLFormElement): void {
	const button = document.getElementById("submit-button");
	if (!(button instanceof HTMLButtonElement)) return;
	button.style.setProperty("--release-ms", `${RELEASE_WINDOW_MS}ms`);
	let timer: number | null = null;
	let releaseTimer: number | null = null;
	let isReady = false;
	const reset = (): void => {
		if (timer !== null) window.clearTimeout(timer);
		if (releaseTimer !== null) window.clearTimeout(releaseTimer);
		timer = null;
		releaseTimer = null;
		isReady = false;
		button.classList.remove("holding", "ready", "submitting");
		button.textContent = t.holdToSubmit;
	};
	const start = (): void => {
		if (timer !== null || isReady) return;
		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}
		const holdMs = HOLD_MIN_MS + Math.random() * (HOLD_MAX_MS - HOLD_MIN_MS);
		button.style.setProperty("--hold-ms", `${Math.round(holdMs)}ms`);
		button.classList.add("holding");
		button.textContent = t.keepHolding;
		timer = window.setTimeout(() => {
			timer = null;
			isReady = true;
			button.classList.add("ready");
			button.textContent = t.releaseToSubmit;
			releaseTimer = window.setTimeout(() => {
				releaseTimer = null;
				reset();
				button.textContent = t.heldTooLong;
			}, RELEASE_WINDOW_MS);
		}, holdMs);
	};
	const finish = (): void => {
		if (timer !== null) window.clearTimeout(timer);
		if (releaseTimer !== null) window.clearTimeout(releaseTimer);
		timer = null;
		releaseTimer = null;
		const shouldSubmit = isReady;
		isReady = false;
		if (!shouldSubmit) {
			reset();
			return;
		}
		// Stay green and settle into a stable "Submitting…" state — no
		// flash back to the idle button between release and the response.
		button.classList.remove("holding");
		button.classList.add("submitting");
		button.innerHTML =
			'<span class="spinner spinner-light" aria-hidden="true"></span> Submitting…';
		void submitApplication(slug, form);
	};
	button.addEventListener("pointerdown", (event) => {
		event.preventDefault();
		start();
	});
	// pointerleave fires right after release on touch (and on mouse when the
	// cursor drifts) — it must not knock a submitting button back to idle.
	const cancelHold = (): void => {
		if (button.classList.contains("submitting")) return;
		reset();
	};
	button.addEventListener("pointerup", finish);
	button.addEventListener("pointerleave", cancelHold);
	button.addEventListener("pointercancel", cancelHold);
	button.addEventListener("keydown", (event) => {
		if (event.key === " " || event.key === "Enter") {
			event.preventDefault();
			if (!event.repeat) start();
		}
	});
	button.addEventListener("keyup", (event) => {
		if (event.key === " " || event.key === "Enter") finish();
	});
}

/** Click-or-drop file selection on a labeled dropzone. */
function wireDropzone(
	zoneId: string,
	inputId: string,
	onFile: (file: File) => void
): void {
	const zone = document.getElementById(zoneId);
	const input = document.getElementById(inputId);
	if (!(zone instanceof HTMLElement) || !(input instanceof HTMLInputElement)) {
		return;
	}
	input.addEventListener("change", () => {
		const file = input.files?.[0];
		if (file) onFile(file);
	});
	zone.addEventListener("dragover", (event) => {
		event.preventDefault();
		zone.classList.add("dragging");
	});
	zone.addEventListener("dragleave", () => zone.classList.remove("dragging"));
	zone.addEventListener("drop", (event) => {
		event.preventDefault();
		zone.classList.remove("dragging");
		const file = event.dataTransfer?.files?.[0];
		if (!file) return;
		const transfer = new DataTransfer();
		transfer.items.add(file);
		input.files = transfer.files;
		onFile(file);
	});
}

/** Spinner + "Reading <file>…" inside a dropzone label. */
function setReadingState(label: HTMLElement, filename: string): void {
	label.innerHTML = `<span class="spinner" aria-hidden="true"></span> ${esc(t.readingFile)} ${esc(filename)}…`;
}

/** Cover-letter file → plain text into the editable textarea (no LLM). */
async function extractLetter(slug: string, file: File): Promise<void> {
	const label = document.getElementById("letter-name");
	const textarea = document.getElementById("message");
	if (!(textarea instanceof HTMLTextAreaElement)) return;
	if (label) setReadingState(label, file.name);
	const data = new FormData();
	data.set("file", file);
	const response = await fetch(
		`/api/v2/jobs/${encodeURIComponent(slug)}/extract_text/`,
		{ method: "POST", body: data }
	);
	if (!response.ok) {
		if (label) {
			label.textContent = t.letterUnreadable;
		}
		return;
	}
	const result = (await response.json()) as { text: string };
	textarea.value = result.text.trim();
	if (label) label.textContent = `${file.name} — ${t.letterInserted}`;
}

function prefillField(name: string, value: string): void {
	const input = document.getElementById(name);
	if (input instanceof HTMLInputElement && input.value === "" && value) {
		input.value = value;
	}
}

function renderParsedPanel(draft: ParsedDraft): void {
	const panel = document.getElementById("parsed-panel");
	if (!panel) return;
	const contact = [...draft.emails, ...draft.phones].map(esc).join(" · ");
	const roles = draft.roles
		.slice(0, 4)
		.map((role) => {
			const period = role.current
				? `${esc(role.start_date)} – now`
				: [role.start_date, role.end_date].filter(Boolean).map(esc).join(" – ");
			return `<li>${esc(role.title)}${role.company ? ` — ${esc(role.company)}` : ""}${period ? ` <span class="muted">(${period})</span>` : ""}</li>`;
		})
		.join("");
	const skills = draft.skills.slice(0, 12).map(esc).join(", ");
	panel.innerHTML = `
		<div class="parsed-panel">
			<div class="parsed-title">${esc(t.parsedTitle)}</div>
			<div class="parsed-name">${esc(draft.name)}${draft.headline ? ` <span class="muted">· ${esc(draft.headline)}</span>` : ""}</div>
			${contact ? `<div class="parsed-row">${contact}</div>` : ""}
			${roles ? `<ul class="parsed-roles">${roles}</ul>` : ""}
			${skills ? `<div class="parsed-row muted">${skills}</div>` : ""}
		</div>`;
}

async function parseCv(slug: string, file: File, cvName: HTMLElement): Promise<void> {
	parseId = null;
	setReadingState(cvName, file.name);
	const data = new FormData();
	data.set("cv", file);
	const response = await fetch(`/api/v2/jobs/${encodeURIComponent(slug)}/parse_cv/`, {
		method: "POST",
		body: data,
	});
	cvName.textContent = file.name;
	const panel = document.getElementById("parsed-panel");
	if (!response.ok) {
		if (panel) {
			panel.innerHTML = `<div class="parsed-panel muted">${esc(t.parsedFailed)}</div>`;
		}
		return;
	}
	const result = (await response.json()) as ParseCvResponse;
	parseId = result.parse_id;
	renderParsedPanel(result.draft);
	prefillField("applicant_name", result.draft.name);
	prefillField("email", result.draft.emails[0] ?? "");
	prefillField("phone", result.draft.phones[0] ?? "");
	prefillField("linkedin_url", result.linkedin_url);
}

function renderFailure(text: string): void {
	root().innerHTML = `<div class="page"><div class="empty">${esc(text)}</div></div>`;
}

/** The job-ad wizard embeds this page in an iframe and posts unsaved drafts to
 *  it, so its preview IS the public page — same markup, stylesheet and fonts —
 *  rather than a second renderer that can drift out of step. */
function startPreviewMode(): void {
	window.addEventListener("message", (event) => {
		if (event.source !== window.parent) return;
		const message = event.data as { type?: string; ad?: PublicJobDetail };
		if (message.type !== "job-ad-preview" || !message.ad) return;
		renderAd(message.ad, false);
	});
	window.parent.postMessage({ type: "job-ad-preview-ready" }, "*");
}

/** What this page shows, from its address alone.
 *
 * Embedded on a customer's site the slug arrives as ``?job=``: their page owns
 * its own path, so the ad cannot live in it.
 */
async function start(): Promise<void> {
	const isPreview = window.parent !== window && params.get("preview") === "1";
	if (isPreview) {
		startPreviewMode();
		return;
	}
	const mode = embedMode();
	const asked = params.get("lang");
	if (mode !== null) {
		// Drops our page background and outer padding: the customer's page
		// owns both, and the frame is sized to the content that is left.
		document.body.classList.add("embedded");
		reportHeightContinuously();
		// The brand decides the look and where cards link; both are needed
		// before anything is drawn.
		const brand = await loadBrand();
		applyBrand(brand);
		const embedded = params.get("job");
		if (mode === "ad" && embedded) {
			await renderDetail(embedded, brand, asked);
		} else {
			await renderList(brand);
		}
		return;
	}
	const statusToken = currentStatusToken();
	if (statusToken !== null) {
		applyBrand(await loadBrand());
		// The language comes from the application itself, inside.
		await renderStatusPage(statusToken);
		return;
	}
	const brand = await loadBrand();
	applyBrand(brand);
	const slug = currentSlug();
	if (slug === null) {
		await renderList(brand);
		return;
	}
	await renderDetail(slug, brand, asked);
}

void start();
